import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';
import { JobCardInfoModalProps } from '@v2/pages/planboard/types';
import { formatDateConditionally } from '@v2/helpers/time';

import ColorWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';
import TouchableElement from '@components/common/touchableElement';
import { JOB_STATUSES_TEXT, OPERATION_MACHINE_LINK_TYPE } from '@helpers';
import { SCREEN_BORDER_RADIUS, modalFieldBg, PLANBOARD_TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import useJobStatusBadgeConfig from '@v2/pages/planboard/hooks/useJobStatusBadgeConfig';
import { usePlanboardJobNavigation } from '@v2/pages/planboard/hooks/usePlanboardJobNavigation';
import useElapsedTime from '@v2/pages/planboard/hooks/useElapsedTime';
import ModalWrapper from '@components/modal/ModalWrapper';
import JobCardInfoModalRow from './JobCardInfoModalRow';
import JobCardDaysBadge from '@v2/pages/planboard/components/JobCardPreview/JobCardDaysBadge';
import { useCalculateModalPosition } from '@v2/pages/planboard/hooks/useCalculateModalPosition';
import StatusBadge from '@components/common/StatusBadge';
import TimeDisplay from '@components/common/TimeDisplay';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import ModalArrowPointer from '@v2/pages/planboard/components/JobCardPreview/ModalArrowPointer';

const JobCardInfoModal: React.FC<JobCardInfoModalProps> = ({ isOpen, onClose, item, anchorRef, C, scale, font }) => {
  const position = useCalculateModalPosition({ isOpen, anchorRef, scale });
  const { navigateToGeneral } = usePlanboardJobNavigation(item);
  const jobStatusConfig = useJobStatusBadgeConfig({ C, status: item.status });
  const { elapsedTimeValue, shouldRunTime } = useElapsedTime(item);

  // Check if job is scheduled (has ScheduledOn or ProducedOn machine link)
  const isScheduledJob = item.machines?.some((m) =>
    [OPERATION_MACHINE_LINK_TYPE.ScheduledOn, OPERATION_MACHINE_LINK_TYPE.ProducedOn].includes(m.linkType)
  );

  const timePerPiece = useMemo(() => {
    if (!item.quantityTarget) return 0;
    return Math.abs((item.totalTimeScheduledSeconds ?? 0) - (item.setupTimeSeconds ?? 0)) / item.quantityTarget;
  }, [item.totalTimeScheduledSeconds, item.setupTimeSeconds, item.quantityTarget]);

  const handleJobPress = () => {
    onClose();
    navigateToGeneral();
  };

  // Pinned functionality is commented out; to be discussed with Dinant
  // const handlePinnedPress = () => {
  //   console.log('Pinned clicked');
  // };

  const modalWidth = scale(PLANBOARD_TILE_CONFIG.MODAL_WIDTH);

  return (
    <ModalWrapper isVisible={isOpen} C={C} onBackdropPress={onClose} backdropColor="transparent" backdropOpacity={0}>
      <ModalArrowPointer
        position={position}
        scale={scale}
        C={C}
        font={font}
        width={modalWidth}
        maxHeight={position.maxHeight}
        contentContainerStyle={{
          backgroundColor: C(modalFieldBg),
          padding: scale(8),
        }}
      >
        {/* Main Info Section */}
        <View
          style={{
            backgroundColor: C('Background/Light/Field on Panel'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            paddingHorizontal: scale(16),
          }}
        >
          {/* Job Row */}
          <JobCardInfoModalRow
            label={i18n.t('Job')}
            value={
              <TouchableElement onPress={handleJobPress}>
                {() => (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(4), cursor: 'pointer' }}>
                    <Text style={[font['Body'], { color: C('Link/Light/Primary') }]}>{item.productionOperationNumber}</Text>
                    <Svg icon="arrowLight" width={scale(24)} height={scale(24)} color="Link/Light/Primary" />
                  </View>
                )}
              </TouchableElement>
            }
            C={C}
            scale={scale}
            font={font}
          />

          {/* Status Row */}
          <JobCardInfoModalRow
            label={i18n.t('Status')}
            value={
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(6) }}>
                <Text style={[font['Body'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(JOB_STATUSES_TEXT[item.status])}</Text>
                <StatusBadge C={C} config={jobStatusConfig} scale={scale} font={font} />
              </View>
            }
            C={C}
            scale={scale}
            font={font}
          />

          {/* Operation Row */}
          <JobCardInfoModalRow label={i18n.t('Operation')} value={item.operationName} C={C} scale={scale} font={font} />

          {/* Actual Start Row - only for scheduled jobs */}
          {isScheduledJob && (
            <JobCardInfoModalRow
              label={i18n.t('Actual Start')}
              value={formatDateConditionally(item.actualStartDate) || '-'}
              C={C}
              scale={scale}
              font={font}
            />
          )}

          {/* Scheduled Start Row */}
          <JobCardInfoModalRow
            label={i18n.t('Scheduled Start')}
            value={formatDateConditionally(item.scheduledStartDate) || '-'}
            C={C}
            scale={scale}
            font={font}
          />

          {/* Expected Start Row - only for scheduled jobs */}
          {isScheduledJob && (
            <JobCardInfoModalRow
              label={i18n.t('Expected Start')}
              value={formatDateConditionally(item.expectedStartOn) || '-'}
              C={C}
              scale={scale}
              font={font}
            />
          )}

          {/* Days Behind Row - only for scheduled jobs */}
          {isScheduledJob && (
            <JobCardInfoModalRow
              label={i18n.t('Days Behind')}
              value={
                <JobCardDaysBadge
                  scheduledStartDate={item.scheduledStartDate}
                  expectedStartOn={item.expectedStartOn}
                  scale={scale}
                  font={font}
                  C={C}
                />
              }
              C={C}
              scale={scale}
              font={font}
            />
          )}

          {/* Pinned Row is commented out; to be discussed with Dinant*/}
          {/* <TouchableElement onPress={handlePinnedPress}>
            {({ pressHoverColor, boxStyle }) => (
              <View style={[boxStyle, {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingVertical: scale(10),
                borderBottomWidth: 1,
                borderBottomColor: C('Hairline/Light/No trans [mobile]'),
                backgroundColor: C(pressHoverColor('Background/Light/Field on Panel')),
              }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(6) }}>
                  <Svg icon="pin" width={scale(10)} height={scale(14)} color="Label/Light/Primary" />
                  <Text style={[font['Body'], { color: C('Label/Light/Primary') }]}>{i18n.t('Pinned')}</Text>
                </View>
                <Svg icon="chevronRight" width={scale(8)} height={scale(14)} color="Label/Light/Tertiary" />
              </View>
            )}
          </TouchableElement> */}

          {/* Scheduled End Row */}
          <JobCardInfoModalRow
            label={i18n.t('Scheduled End')}
            value={formatDateConditionally(item.scheduledEndDate) || '-'}
            C={C}
            scale={scale}
            font={font}
          />

          {/* Ready Row */}
          <JobCardInfoModalRow
            label={i18n.t('Ready')}
            value={
              <Text style={[font['Body'], { color: C('Label/Light/Tertiary') }]}>
                {item.quantityProduced ?? 0} / {item.quantityTarget ?? 0} Pcs
              </Text>
            }
            C={C}
            scale={scale}
            font={font}
          />

          {/* Linked NC Files Row */}
          <JobCardInfoModalRow label={i18n.t('Linked NC Files')} value={item.linkedNcFiles?.length ?? 0} isLast C={C} scale={scale} font={font} />
        </View>

        {/* Times Label - outside white background */}
        <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary'), marginTop: scale(12), marginBottom: scale(8), marginLeft: scale(16) }]}>
          {i18n.t('TIMES')}
        </Text>

        {/* Times Section */}
        <View
          style={{
            backgroundColor: C('Background/Light/Field on Panel'),
            borderRadius: scale(SCREEN_BORDER_RADIUS),
            paddingHorizontal: scale(16),
          }}
        >
          {/* Setup Time Row */}
          <JobCardInfoModalRow
            label={i18n.t('Setup Time')}
            value={
              <TimeDisplay
                withHMSFormat={false}
                C={C}
                value={item.setupTimeSeconds ?? 0}
                scale={scale}
                font={font}
                fontForLetter="Body"
                fontForSecondLetter="Body"
                fontForDigit="Body"
                fontForSecondDigit="Body"
                includeSecondsAlways={false}
                color="Label/Light/Tertiary"
              />
            }
            C={C}
            scale={scale}
            font={font}
          />

          {/* Time per Piece Row */}
          <JobCardInfoModalRow
            label={i18n.t('Time per Piece')}
            value={
              <TimeDisplay
                withHMSFormat={false}
                C={C}
                value={timePerPiece}
                scale={scale}
                font={font}
                fontForLetter="Body"
                fontForSecondLetter="Body"
                fontForDigit="Body"
                fontForSecondDigit="Body"
                includeSecondsAlways={false}
                color="Label/Light/Tertiary"
              />
            }
            C={C}
            scale={scale}
            font={font}
          />

          {/* Total Paid Time Row */}
          <JobCardInfoModalRow
            label={i18n.t('Total Paid Time')}
            value={
              <TimeDisplay
                withHMSFormat={false}
                C={C}
                value={item.totalTimeScheduledSeconds ?? 0}
                scale={scale}
                font={font}
                fontForLetter="Body"
                fontForSecondLetter="Body"
                fontForDigit="Body"
                fontForSecondDigit="Body"
                includeSecondsAlways={false}
                color="Label/Light/Tertiary"
              />
            }
            C={C}
            scale={scale}
            font={font}
          />

          {/* Elapsed Time Row */}
          <JobCardInfoModalRow
            label={i18n.t('Elapsed Time')}
            value={
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(4) }}>
                <Svg icon="clock" width={scale(16)} height={scale(16)} color="System/Light/Blue" />
                <TimeDisplayInStaticFormat
                  timeValue={elapsedTimeValue}
                  shouldRunTime={shouldRunTime}
                  withHMSFormat={false}
                  C={C}
                  scale={scale}
                  font={font}
                  fontForDigit="Body"
                  fontForSecondDigit="Body"
                  fontForLetter="Body"
                  fontForSecondLetter="Body"
                  includeSecondsAlways={false}
                  color="Label/Light/Tertiary"
                />
              </View>
            }
            isLast
            C={C}
            scale={scale}
            font={font}
          />
        </View>
      </ModalArrowPointer>
    </ModalWrapper>
  );
};

export default ColorWrapper(JobCardInfoModal);
